import { useTranslation } from 'react-i18next'
import { Button, TextField } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useContext, useEffect, useState, useRef } from 'react'

import './styles/index.css'
import {
  countriesCurrencies,
  customersDocumentType,
  mexicanBankAccountType,
  chileanBankAccountType,
  peruvianBankAccountType,
  brazilianBankAccountType,
  customersBankAccountType,
  argentinianBankAccountType,
  chileanCustomersDocumentType,
  mexicanCustomersDocumentType,
  peruvianCustomersDocumentType,
  brazilianCustomersDocumentType,
  argentinianCustomersDocumentType
} from '../../lib'
import Dialog from '../common/dialog'
import { requestAccessToken } from '../../api/auth.api'
import BankIcon from '../../assets/icons/bank-icon.svg'
import DangerIcon from '../../assets/icons/danger-icon.svg'
import AccountIcon from '../../assets/icons/account-icon.svg'
import ContactsIcon from '../../assets/icons/contacts-icon.svg'
import MoneyGreenIcon from '../../assets/icons/money-icon-green.svg'
import { PaymentToReviewTableRow } from './payment-to-review-table-row'
import { numberWithCommas, numberWithDots } from '../../utils/common.utils'
import { PaymentBackgroundNovelties } from './payment-background-novelties'
import { baseButtonBlueBg, textFieldWhiteBg } from '../../theme/custom-styles'
import PaymentReferenceIcon from '../../assets/icons/payment-reference-icon.svg'
import BackgroundResearchIcon from '../../assets/icons/background-research-icon.svg'
import { BankAccount, Customer, Payment, SupportTicket, FormError } from '../../types'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../../contexts'
import { getPaymentTickets, returnPaymentData, sendPaymentComplianceRequest, sendPaymentComplianceResponse } from '../../api/payments.api'
import {getAttachmentFilePreSignedURL, getDownloadAttachmentFilePreSignedURL, uploadAttachmentFileToS3, resolveDocumentsAccessToken } from '../../api/documents.api'

export const PaymentToReviewDetailsTable = ({
  paymentData,
  paymentCustomerData,
  hideReviewButtonInRow,
  showBackgroundNoveltiesData,
  handleOpenDialogToResolveNovelty
}: {
  paymentCustomerData: Customer
  hideReviewButtonInRow?: boolean
  showBackgroundNoveltiesData?: boolean
  paymentData: Payment | Partial<Payment> | null
  handleOpenDialogToResolveNovelty?: (noveltyType: string) => void
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const location = useLocation()
  const paymentBackgroundNoveltiesRef = useRef()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateLoadingStatus } = useContext(AppStateContext)
  const { t } = useTranslation(['payments-to-review-management'])
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [errors, setErrors] = useState<FormError>({})
  const [paymentActionReason, setPaymentActionReason] = useState('')
  const [customerIdDocumentInfo, setCustomerIdDocumentInfo] = useState('')
  const [bankAccountDataInfo, setBankAccountDataInfo] = useState('')
  const [hasEmptyBankName, setHasEmptyBankName] = useState<boolean>(false)
  const [hasUnknownBankName, setHasUnknownBankName] = useState<boolean>(false)
  const [hasWrongDocumentType, setHasWrongDocumentType] = useState<boolean>(false)
  const [hasWrongPaymentAmount, setHasWrongPaymentAmount] = useState<boolean>(false)
  const [hasEmptyPaymentAmount, setHasEmptyPaymentAmount] = useState<boolean>(false)
  const [hasEmptyRegisteredName, setHasEmptyRegisteredName] = useState<boolean>(false)
  const [hasEmptyDocumentNumber, setHasEmptyDocumentNumber] = useState<boolean>(false)
  const [hasWrongDocumentNumber, setHasWrongDocumentNumber] = useState<boolean>(false)
  const [hasEmptyBankAccountType, setHasEmptyBankAccountType] = useState<boolean>(false)
  const [hasWrongBankAccountType, setHasWrongBankAccountType] = useState<boolean>(false)
  const [paymentTicketsData, setPaymentTicketsData] = useState<Array<SupportTicket>>([])
  const [hasEmptyPaymentReference, setHasEmptyPaymentReference] = useState<boolean>(false)
  const [hasEmptyBankAccountNumber, setHasEmptyBankAccountNumber] = useState<boolean>(false)
  const [hasWrongBankAccountNumber, setHasWrongBankAccountNumber] = useState<boolean>(false)
  const [hasBackgroundResearchResult, setHasBackgroundResearchResult] = useState<boolean>(false)
  const [hasDuplicatePaymentReference, setHasDuplicatePaymentReference] = useState<boolean>(false)
  const [hasDuplicateBankAccountNumber, setHasDuplicateBankAccountNumber] = useState<boolean>(false)
  const [documentNoveltyDangerDescription, setDocumentNoveltyDangerDescription] = useState<string>('')
  const [, /*bankAccountData */ setBankAccountData] = useState<Partial<BankAccount> | null | undefined>(null)
  const [documentNoveltyWarningDescription, setDocumentNoveltyWarningDescription] = useState<string>('')
  const [dialogConfirmReturnPaymentIsOpen, setDialogConfirmReturnPaymentIsOpen] = useState<boolean>(false)
  const [bankAccountNoveltyDangerDescription, setBankAccountNoveltyDangerDescription] = useState<string>('')
  const [hasBankAccountNumberNotRegistered, setHasBankAccountNumberNotRegistered] = useState<boolean>(false)
  const [bankAccountNoveltyWarningDescription, setBankAccountNoveltyWarningDescription] = useState<string>('')
  const [dialogConfirmReturnPaymentIsLoading, setDialogConfirmReturnPaymentIsLoading] = useState<boolean>(false)
  const [paymentAmountNoveltyDangerDescription, setPaymentAmountNoveltyDangerDescription] = useState<string>('')
  const [registeredNameNoveltyDangerDescription, setRegisteredNameNoveltyDangerescription] = useState<string>('')
  const [paymentAmountNoveltyWarningDescription, setPaymentAmountNoveltyWarningDescription] = useState<string>('')
  const [backgroundResearchResult, setBackgroundResearchResult] = useState<{ riskLevel: number }>({ riskLevel: 0 })
  const [paymentReferenceNoveltyDangerDescription, setPaymentReferenceNoveltyDangerDescription] = useState<string>('')

  useEffect(() => {
    handleGetPaymentTickets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (paymentData && paymentCustomerData) {
      const banksAccountsDataByCustomer =
        Array.isArray(paymentCustomerData.bankAccountsData) && paymentCustomerData.bankAccountsData.length > 0
          ? paymentCustomerData.bankAccountsData?.find((bankAccount) => {
              if (bankAccount.bankNumber === paymentData.bankAccountNumber) {
                // Esto lo hacemos por el formato para el cliente
                if (bankAccount.bankAccountType === '27') {
                  bankAccount.bankAccountType = '1'
                } else if (bankAccount.bankAccountType === '37') {
                  bankAccount.bankAccountType = '2'
                } else if (bankAccount.bankAccountType === '1') {
                  bankAccount.bankAccountType = '1'
                } else if (bankAccount.bankAccountType === '2') {
                  bankAccount.bankAccountType = '2'
                }
                // } else {
                //   bankAccount.bankAccountType = bankAccount.bankAccountType
                // }

                return bankAccount
              }

              return null
            })
          : paymentData?.bankAccountType && paymentData.bankName && paymentData.bankAccountNumber
            ? {
                bankName: paymentData.bankName,
                bankNumber: paymentData.bankAccountNumber,
                // Esto lo hacemos por el formato para el cliente
                bankAccountType: paymentData.bankAccountType === '27' ? '1' : paymentData.bankAccountType === '37' ? '2' : '3'
              }
            : null

      setBankAccountData(banksAccountsDataByCustomer)
      let documentNoveltyDangerDescriptionSetted = false

      if (Array.isArray(paymentData.novelties)) {
        for (let i = 0; i < paymentData.novelties.length; i++) {
          const paymentNovelty = paymentData.novelties[i]

          if (paymentNovelty.type === 'background-research') {
            if (paymentNovelty.resolved === false) {
              setHasBackgroundResearchResult(true)
              const riskLevel =
                Number(paymentNovelty.backgroundResearchRiskLevelByDocumentNumber) > Number(paymentNovelty.backgroundResearchRiskLevelByName)
                  ? Number(paymentNovelty.backgroundResearchRiskLevelByDocumentNumber)
                  : Number(paymentNovelty.backgroundResearchRiskLevelByDocumentNumber) < Number(paymentNovelty.backgroundResearchRiskLevelByName)
                    ? Number(paymentNovelty.backgroundResearchRiskLevelByName)
                    : Number(paymentNovelty.backgroundResearchRiskLevelByDocumentNumber) === Number(paymentNovelty.backgroundResearchRiskLevelByName) && Number(paymentNovelty.backgroundResearchRiskLevelByDocumentNumber)

              setBackgroundResearchResult({ riskLevel: Number(riskLevel) })
            } else {
              setHasBackgroundResearchResult(false)
              setBackgroundResearchResult({ riskLevel: 0 })
            }
          }

          if (paymentNovelty.type === 'empty-bank-name') {
            if (paymentNovelty.resolved === false) {
              setHasEmptyBankName(true)
              setBankAccountNoveltyWarningDescription(t('noveltiesDescription.bankName'))
            } else {
              setHasEmptyBankName(false)
              setBankAccountNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'empty-bank-code') {
            if (paymentNovelty.resolved === false) {
              setHasEmptyBankName(true)
              setBankAccountNoveltyWarningDescription(t('noveltiesDescription.emptyBankCode'))
            } else {
              setHasEmptyBankName(false)
              setBankAccountNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'unknown-bank-code') {
            if (paymentNovelty.resolved === false) {
              setHasUnknownBankName(true)
              setBankAccountNoveltyWarningDescription(t('noveltiesDescription.unknownBankCode'))
            } else {
              setHasUnknownBankName(false)
              setBankAccountNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'unknown-bank-name') {
            if (paymentNovelty.resolved === false) {
              setHasUnknownBankName(true)
              setBankAccountNoveltyWarningDescription(t('noveltiesDescription.unknownBankName'))
            } else {
              setHasUnknownBankName(false)
              setBankAccountNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'empty-bank-account-type') {
            if (paymentNovelty.resolved === false) {
              setHasEmptyBankAccountType(true)
              setBankAccountNoveltyWarningDescription(t('noveltiesDescription.emptyBankAccountType'))
            } else {
              setHasEmptyBankAccountType(false)
              setBankAccountNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'wrong-bank-account-type') {
            if (paymentNovelty.resolved === false) {
              setHasWrongBankAccountType(true)
              setBankAccountNoveltyWarningDescription(t('noveltiesDescription.wrongBankAccountType'))
            } else {
              setHasWrongBankAccountType(false)
              setBankAccountNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'wrong-bank-account-number') {
            if (paymentNovelty.resolved === false) {
              setHasWrongBankAccountNumber(true)
              const noveltyDescription =
                paymentNovelty.wrongBankAccountWarning === 'The bank account number has to have only digits.' ? t('noveltiesDescription.wrongBankAccountNumber') : String(paymentNovelty.wrongBankAccountWarning)
              setBankAccountNoveltyDangerDescription(noveltyDescription)
            } else {
              setHasWrongBankAccountNumber(false)
              setBankAccountNoveltyDangerDescription('')
            }
          }

          if (paymentNovelty.type === 'bank-account-number-not-registered') {
            if (paymentNovelty.resolved === false) {
              setHasBankAccountNumberNotRegistered(true)
              setBankAccountNoveltyWarningDescription(t('noveltiesDescription.bankAccountNumberNotRegistered'))
            } else {
              setHasBankAccountNumberNotRegistered(false)
              setBankAccountNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'empty-bank-account-number') {
            if (paymentNovelty.resolved === false) {
              setHasEmptyBankAccountNumber(true)
              setBankAccountNoveltyDangerDescription(t('noveltiesDescription.emptyBankAccountNumber'))
            } else {
              setHasEmptyBankAccountNumber(false)
              setBankAccountNoveltyDangerDescription('')
            }
          }

          if (paymentNovelty.type === 'duplicate-bank-account-number') {
            if (paymentNovelty.resolved === false) {
              setHasDuplicateBankAccountNumber(true)
              setBankAccountNoveltyDangerDescription(t('noveltiesDescription.duplicateBankAccountNumber'))
            } else {
              setHasDuplicateBankAccountNumber(false)
              setBankAccountNoveltyDangerDescription('')
            }
          }

          if (paymentNovelty.type === 'wrong-document-type' && !paymentData.unidentifiedCustomerId) {
            if (paymentNovelty.resolved === false) {
              setHasWrongDocumentType(true)
              setDocumentNoveltyWarningDescription(t('noveltiesDescription.wrongDocumentType'))
            } else {
              setHasWrongDocumentType(false)
              setDocumentNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'wrong-document-number' && !paymentData.unidentifiedCustomerId) {
            if (paymentNovelty.resolved === false) {
              setHasWrongDocumentNumber(true)
              documentNoveltyDangerDescriptionSetted = true
              setDocumentNoveltyDangerDescription(t('noveltiesDescription.wrongDocumentNumber'))
            } else {
              setHasWrongDocumentNumber(false)
              setDocumentNoveltyDangerDescription('')
              documentNoveltyDangerDescriptionSetted = false
            }
          }

          if (paymentNovelty.type === 'empty-document-number' && !paymentData.unidentifiedCustomerId) {
            if (paymentNovelty.resolved === false) {
              setHasEmptyDocumentNumber(true)
              documentNoveltyDangerDescriptionSetted = true
              setDocumentNoveltyDangerDescription(t('noveltiesDescription.emptyDocumentNumber'))
            } else {
              setHasEmptyDocumentNumber(false)
              setDocumentNoveltyDangerDescription('')
              documentNoveltyDangerDescriptionSetted = false
            }
          }

          if (paymentNovelty.type === 'wrong-payment-amount') {
            if (paymentNovelty.resolved === false) {
              setHasWrongPaymentAmount(true)
              setPaymentAmountNoveltyWarningDescription(t('noveltiesDescription.wrongPaymentAmount'))
            } else {
              setHasWrongPaymentAmount(false)
              setPaymentAmountNoveltyWarningDescription('')
            }
          }

          if (paymentNovelty.type === 'empty-payment-amount') {
            if (paymentNovelty.resolved === false) {
              setHasEmptyPaymentAmount(true)
              setPaymentAmountNoveltyDangerDescription(t('noveltiesDescription.emptyPaymentAmount'))
            } else {
              setHasEmptyPaymentAmount(false)
              setPaymentAmountNoveltyDangerDescription('')
            }
          }

          if (paymentNovelty.type === 'empty-registered-name' && !paymentData.unidentifiedCustomerId) {
            if (paymentNovelty.resolved === false) {
              setHasEmptyRegisteredName(true)
              setRegisteredNameNoveltyDangerescription(t('noveltiesDescription.emptyRegisteredName'))
            } else {
              setHasEmptyRegisteredName(false)
              setRegisteredNameNoveltyDangerescription('')
            }
          }

          if (paymentNovelty.type === 'empty-payment-reference') {
            if (paymentNovelty.resolved === false) {
              setHasEmptyPaymentReference(true)
              setPaymentReferenceNoveltyDangerDescription(t('noveltiesDescription.emptyPaymentReference'))
            } else {
              setHasEmptyPaymentReference(false)
              setPaymentReferenceNoveltyDangerDescription('')
            }
          }

          if (paymentNovelty.type === 'duplicate-payment-reference') {
            if (paymentNovelty.resolved === false) {
              setHasDuplicatePaymentReference(true)
              setPaymentReferenceNoveltyDangerDescription(t('noveltiesDescription.duplicatePaymentReference'))
            } else {
              setHasDuplicatePaymentReference(false)
              setPaymentReferenceNoveltyDangerDescription('')
            }
          }
        }
      }

      if (paymentData.unidentifiedCustomerId && !documentNoveltyDangerDescriptionSetted) {
        setDocumentNoveltyDangerDescription(t('noveltiesDescription.unknownDocument'))
      }

      if (selectedCountry === 'COL') {
        if (paymentCustomerData && paymentCustomerData.documentType === 'CE') {
          setCustomerIdDocumentInfo(`${(customersDocumentType as any)['E']}: ${numberWithDots(String(paymentCustomerData.documentNumber))}`)
        }

        if (paymentCustomerData && (customersDocumentType as any)[paymentCustomerData.documentType ?? 'C'] !== 'undefined' && numberWithDots(String(paymentCustomerData.documentNumber)) !== 'undefined') {
          setCustomerIdDocumentInfo(`${(customersDocumentType as any)[paymentCustomerData.documentType || 'C']}: ${numberWithDots(String(paymentCustomerData.documentNumber))}`)
        }

        if (
          banksAccountsDataByCustomer &&
          typeof (customersBankAccountType as any)[banksAccountsDataByCustomer.bankAccountType ?? '3'] !== 'undefined' &&
          banksAccountsDataByCustomer.bankName !== 'undefined' &&
          banksAccountsDataByCustomer.bankNumber !== 'undefined'
        ) {
          setBankAccountDataInfo(`${(customersBankAccountType as any)[banksAccountsDataByCustomer.bankAccountType ?? '3']} | ${banksAccountsDataByCustomer.bankName} - No. ${banksAccountsDataByCustomer.bankNumber}`)
        }
      }

      if (selectedCountry === 'ARG') {
        setCustomerIdDocumentInfo(`${(argentinianCustomersDocumentType as any)[paymentCustomerData.documentType?.toLowerCase() ?? 'dni']}: ${String(paymentCustomerData.documentNumber)}`)
        setBankAccountDataInfo(
          `${(argentinianBankAccountType as any)[banksAccountsDataByCustomer?.bankAccountType.toLowerCase() ?? 'savings']} | ${banksAccountsDataByCustomer?.bankName} - No. ${banksAccountsDataByCustomer?.bankNumber}`
        )
      }

      if (selectedCountry === 'CHI') {
        setCustomerIdDocumentInfo(`${(chileanCustomersDocumentType as any)[paymentCustomerData.documentType?.toLowerCase() ?? 'rut']}: ${String(paymentCustomerData.documentNumber)}`)
        setBankAccountDataInfo(
          `${(chileanBankAccountType as any)[banksAccountsDataByCustomer?.bankAccountType.toLowerCase() ?? 'savings']} | ${banksAccountsDataByCustomer?.bankName} - No. ${banksAccountsDataByCustomer?.bankNumber}`
        )
      }

      if (selectedCountry === 'PER') {
        setCustomerIdDocumentInfo(`${(peruvianCustomersDocumentType as any)[paymentCustomerData.documentType?.toLowerCase() ?? 'dni']}: ${String(paymentCustomerData.documentNumber)}`)
        setBankAccountDataInfo(
          `${(peruvianBankAccountType as any)[banksAccountsDataByCustomer?.bankAccountType.toLowerCase() ?? 'savings']} | ${banksAccountsDataByCustomer?.bankName} - No. ${banksAccountsDataByCustomer?.bankNumber}`
        )
      }

      if (selectedCountry === 'BRA') {
        setCustomerIdDocumentInfo(`${(brazilianCustomersDocumentType as any)[paymentCustomerData.documentType?.toLowerCase() ?? 'cpf']}: ${String(paymentCustomerData.documentNumber)}`)
        setBankAccountDataInfo(
          `${(brazilianBankAccountType as any)[banksAccountsDataByCustomer?.bankAccountType.toLowerCase() ?? 'savings']} | ${banksAccountsDataByCustomer?.bankName} - No. ${banksAccountsDataByCustomer?.bankNumber}`
        )
      }

      if (selectedCountry === 'MEX') {
        setCustomerIdDocumentInfo(`${(mexicanCustomersDocumentType as any)[paymentCustomerData.documentType?.toLowerCase() ?? 'rfc']}: ${String(paymentCustomerData.documentNumber)}`)
        setBankAccountDataInfo(
          `${(mexicanBankAccountType as any)[banksAccountsDataByCustomer?.bankAccountType.toLowerCase() ?? 'clabe']} | ${banksAccountsDataByCustomer?.bankName} - No. ${banksAccountsDataByCustomer?.bankNumber}`
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData])

  const handleGetPaymentTickets = async () => {
    const {
      data: { accessToken }
    } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
    const {
      data: { paymentTickets }
    } = await getPaymentTickets(String(paymentData?._id), accessToken)
    console.log('paymentTickets', paymentTickets)

    setPaymentTicketsData(paymentTickets)
  }

  const handleSendCompliancePaymentResponse = async (responseToSend: string, supportTicketAttachments: Array<{ file: File | null; fileType: string; filename: string; base64: string }>) => {
    setErrors({})

    if (!responseToSend) {
      setErrors({ responseToSend: t('complianceProcess.errors.sendResponse') })
      return
    }

    try {
      updateLoadingStatus?.(true)
      const supportTicketAttachmentsData = []

      if (supportTicketAttachments.length > 0) {
        for (let i = 0; i < supportTicketAttachments.length; i++) {
          const supportTicketAttachment = supportTicketAttachments[i]

          const attachmentFileData = {
            attachmentFileType: supportTicketAttachment.fileType,
            attachmentFileSystemName: supportTicketAttachment.filename
          }

          const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
          const attachmentFileToUploadPreSignedURL = await getAttachmentFilePreSignedURL(attachmentFileData, accessToken)
          await uploadAttachmentFileToS3(attachmentFileToUploadPreSignedURL.data, supportTicketAttachment.fileType, supportTicketAttachment.file as File)

          supportTicketAttachmentsData.push({
            filename: supportTicketAttachment.filename,
            fileType: supportTicketAttachment.fileType,
            attachmentFileToUploadPreSignedURL: attachmentFileToUploadPreSignedURL.data
          })
        }
      }

      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { paymentTicketData }
      } = await sendPaymentComplianceResponse({ paymentId: String(paymentData?._id), responseToSend, supportTicketAttachmentsData, from: location.pathname }, accessToken)

      const paymentBackgroundNovelties = paymentBackgroundNoveltiesRef.current as unknown as { handleResetToInitState: () => void }
      paymentBackgroundNovelties.handleResetToInitState()

      const paymentTicketsUpdatedData = [...paymentTicketsData]
      const paymentTicketToUpdateIndex = paymentTicketsUpdatedData.findIndex((paymentTicket) => String(paymentTicket.entityId) === String(paymentTicketData.entityId))
      paymentTicketsUpdatedData[paymentTicketToUpdateIndex] = { ...paymentTicketData }
      setPaymentTicketsData(paymentTicketsUpdatedData)
      updateNewNotificationStatus?.(true, 'payment-request-added')

      updateLoadingStatus?.(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      updateLoadingStatus?.(false)
    }
  }

  const handleSendCompliancePaymentRequest = async (requestToSend: string) => {
    setErrors({})

    if (!requestToSend) {
      setErrors({ requestToSend: t('complianceProcess.errors.sendRequest') })
      return
    }

    try {
      updateLoadingStatus?.(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { paymentTicketData }
      } = await sendPaymentComplianceRequest({ paymentId: String(paymentData?._id), requestToSend, from: location.pathname }, accessToken)

      const paymentBackgroundNovelties = paymentBackgroundNoveltiesRef.current as unknown as { handleResetToInitState: () => void }
      paymentBackgroundNovelties.handleResetToInitState()
      const paymentTicketsUpdatedData = [...paymentTicketsData]

      if (paymentTicketsUpdatedData.length === 0) {
        paymentTicketsUpdatedData.push(paymentTicketData)
        setPaymentTicketsData(paymentTicketsUpdatedData)
      } else {
        const paymentTicketToUpdateIndex = paymentTicketsUpdatedData.findIndex((paymentTicket) => String(paymentTicket.entityId) === String(paymentTicketData.entityId))
        paymentTicketsUpdatedData[paymentTicketToUpdateIndex] = { ...paymentTicketData }
        setPaymentTicketsData(paymentTicketsUpdatedData)
      }

      updateNewNotificationStatus?.(true, 'payment-request-added')
      updateLoadingStatus?.(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      updateLoadingStatus?.(false)
    }
  }

  const handleDownloadAttachmentFile = async (attachmentFilename: string) => {
    setErrors({})

    try {
      updateLoadingStatus?.(true)
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const downloadAttachmentFilePreSignedURL = await getDownloadAttachmentFilePreSignedURL({ attachmentFilename }, accessToken)

      window.open(downloadAttachmentFilePreSignedURL.data, '_blank')
      updateLoadingStatus?.(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      updateLoadingStatus?.(false)
    }
  }

  const handleReturnPayment = async () => {
    try {
      setErrors({})

      if (!paymentActionReason) {
        setErrors({
          paymentActionReason: t('confirmSetBackgroundResearchReasonModal.errors.emptyField')
        })
        return
      }

      setDialogConfirmReturnPaymentIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await returnPaymentData(String(paymentData?._id), { paymentActionReason }, accessToken)
      const {
        data: { paymentTickets }
      } = await getPaymentTickets(String(paymentData?._id), accessToken)

      setPaymentTicketsData(paymentTickets)
      setDialogConfirmReturnPaymentIsLoading(false)
      setDialogConfirmReturnPaymentIsOpen(false)
      updateNewNotificationStatus?.(true, 'payment-rejected-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogConfirmReturnPaymentIsLoading(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="payment-to-review-details-table-container">
      <div className="table-header-container">
        <div className="left-content">
          <div className="icon-container">
            <img
              src={AccountIcon}
              alt="account-icon"
            />
          </div>
          <div className="header-info-subtitle">{t('noveltiesTable.customerName')}</div>
        </div>
        <div className="right-content">
          <div className={hasEmptyRegisteredName || paymentData?.unidentifiedCustomerId ? 'header-info-data payment-to-review-details-danger-text' : 'header-info-data'}>
            {(hasEmptyRegisteredName || paymentData?.unidentifiedCustomerId) && (
              <div className="info-data-container">
                <div className="icon-container">
                  <img
                    src={DangerIcon}
                    alt="danger-icon"
                  />
                </div>
                <div
                  className="info-data-text"
                  style={{ color: '#EF4444' }}
                >
                  {paymentData?.unidentifiedCustomerId ? t('noveltiesDescription.unidentifiedName') : registeredNameNoveltyDangerDescription}
                </div>
                -
              </div>
            )}
            {paymentCustomerData &&
              paymentCustomerData?.registeredFullName !== '' &&
              paymentCustomerData?.registeredFullName?.toUpperCase() !== 'NULL' &&
              paymentCustomerData?.registeredFullName?.toUpperCase() !== 'UNIDENTIFIED' && <div>{paymentCustomerData ? paymentCustomerData?.registeredFullName?.toUpperCase() : t('noveltiesTable.loading')}</div>}
          </div>
          {(hasEmptyRegisteredName || paymentData?.unidentifiedCustomerId) && (
            <div className="payment-to-review-review-button-container">
              <Button
                variant="text"
                sx={baseButtonBlueBg}
                onClick={() => handleOpenDialogToResolveNovelty?.('identification-document')}
              >
                {t('noveltiesTable.reviewButtonText')}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="table-body-container">
        <PaymentToReviewTableRow
          displayWarningIcon={false}
          icon={PaymentReferenceIcon}
          displayBackgroundResearchData={false}
          hideReviewButtonInRow={hideReviewButtonInRow}
          subtitle={t('noveltiesTable.referenceNumber')}
          backgroundResearchData={backgroundResearchResult}
          isDangerInfo={hasEmptyPaymentReference || hasDuplicatePaymentReference}
          displayDangerIcon={hasEmptyPaymentReference || hasDuplicatePaymentReference}
          handleOpenDialogToResolveNovelty={() => handleOpenDialogToResolveNovelty?.('payment-reference')}
          infoData={
            userSession?.role.indexOf('-client') === -1 && String(paymentData?.referenceNumber) !== 'null' && String(paymentData?.referenceNumber) !== 'undefined'
              ? String(paymentData?.referenceNumber)
              : userSession?.role.includes('-client') && String(paymentData?.referenceNumber?.split('-')[1]) !== 'null' && String(paymentData?.referenceNumber?.split('-')[1]) !== 'undefined'
                ? String(paymentData?.referenceNumber?.split('-')[1])
                : ''
          }
          dangerInfoData={paymentReferenceNoveltyDangerDescription && (hasEmptyPaymentReference || hasDuplicatePaymentReference) ? paymentReferenceNoveltyDangerDescription : ''}
        />
        <PaymentToReviewTableRow
          icon={ContactsIcon}
          displayBackgroundResearchData={false}
          displayWarningIcon={hasWrongDocumentType}
          hideReviewButtonInRow={hideReviewButtonInRow}
          subtitle={t('noveltiesTable.idSubtitleName')}
          backgroundResearchData={backgroundResearchResult}
          handleOpenDialogToResolveNovelty={() => handleOpenDialogToResolveNovelty?.('identification-document')}
          displayDangerIcon={hasEmptyDocumentNumber || hasWrongDocumentNumber || !!paymentData?.unidentifiedCustomerId}
          warningInfoData={documentNoveltyWarningDescription && hasWrongDocumentType ? documentNoveltyWarningDescription : ''}
          isDangerInfo={hasEmptyDocumentNumber || hasWrongDocumentNumber || hasWrongDocumentType || !!paymentData?.unidentifiedCustomerId}
          dangerInfoData={documentNoveltyDangerDescription && (hasEmptyDocumentNumber || hasWrongDocumentNumber || !!paymentData?.unidentifiedCustomerId) ? documentNoveltyDangerDescription : ''}
          infoData={customerIdDocumentInfo}
        />
        <PaymentToReviewTableRow
          icon={BankIcon}
          displayBackgroundResearchData={false}
          subtitle={t('noveltiesTable.bankAccount')}
          hideReviewButtonInRow={hideReviewButtonInRow}
          backgroundResearchData={backgroundResearchResult}
          handleOpenDialogToResolveNovelty={() => handleOpenDialogToResolveNovelty?.('bank-account')}
          displayWarningIcon={hasWrongBankAccountType || hasEmptyBankAccountType || hasBankAccountNumberNotRegistered || hasEmptyBankName || hasUnknownBankName}
          displayDangerIcon={hasWrongBankAccountNumber || hasEmptyBankAccountNumber || hasDuplicateBankAccountNumber}
          dangerInfoData={bankAccountNoveltyDangerDescription && (hasWrongBankAccountNumber || hasEmptyBankAccountNumber || hasDuplicateBankAccountNumber) ? bankAccountNoveltyDangerDescription : ''}
          warningInfoData={
            bankAccountNoveltyWarningDescription && (hasWrongBankAccountType || hasEmptyBankAccountType || hasBankAccountNumberNotRegistered || hasEmptyBankName || hasUnknownBankName)
              ? bankAccountNoveltyWarningDescription
              : ''
          }
          isDangerInfo={
            hasWrongBankAccountType ||
            hasEmptyBankAccountType ||
            hasBankAccountNumberNotRegistered ||
            hasEmptyBankName ||
            hasUnknownBankName ||
            hasWrongBankAccountNumber ||
            hasEmptyBankAccountNumber ||
            hasDuplicateBankAccountNumber
          }
          infoData={bankAccountDataInfo}
        />
        <PaymentToReviewTableRow
          icon={MoneyGreenIcon}
          hideReviewButtonInRow={hideReviewButtonInRow}
          isDangerInfo={hasWrongPaymentAmount || hasEmptyPaymentAmount}
          displayDangerIcon={hasEmptyPaymentAmount}
          displayBackgroundResearchData={false}
          displayWarningIcon={hasWrongPaymentAmount}
          subtitle={t('noveltiesTable.amountToPay')}
          backgroundResearchData={backgroundResearchResult}
          infoData={paymentData?.amount && !isNaN(Number(paymentData?.amount)) && Number(paymentData?.amount) > 0 ? `$ ${numberWithCommas(String(paymentData?.amount))} ${currency.toUpperCase()}` : ''}
          handleOpenDialogToResolveNovelty={() => handleOpenDialogToResolveNovelty?.('payment-amount')}
          dangerInfoData={paymentAmountNoveltyDangerDescription && hasEmptyPaymentAmount ? paymentAmountNoveltyDangerDescription : ''}
          warningInfoData={paymentAmountNoveltyWarningDescription && hasWrongPaymentAmount ? paymentAmountNoveltyWarningDescription : ''}
        />
        <PaymentToReviewTableRow
          displayDangerIcon={false}
          displayWarningIcon={false}
          icon={BackgroundResearchIcon}
          isDangerInfo={hasBackgroundResearchResult}
          subtitle={t('noveltiesTable.backgroundResearch')}
          backgroundResearchData={backgroundResearchResult}
          displayBackgroundResearchData={hasBackgroundResearchResult}
          infoData={hasBackgroundResearchResult ? '' : t('noveltiesTable.withoutNovelties')}
          handleOpenDialogToResolveNovelty={() => handleOpenDialogToResolveNovelty?.('background-research')}
          hideReviewButtonInRow={
            String(userSession?.belongsTo) === 'holdings' ||
            (String(userSession?.belongsTo) !== 'holdings' &&
              (paymentData?.status === 'paid' || paymentData?.status === 'reviewed' || paymentData?.status === 'validated' || paymentData?.status === 'processing' || paymentData?.status === 'completed'))
          }
        />
        {showBackgroundNoveltiesData && (
          <PaymentBackgroundNovelties
            errors={errors}
            paymentData={paymentData}
            ref={paymentBackgroundNoveltiesRef}
            paymentTicketsData={paymentTicketsData}
            handleDownloadAttachmentFile={handleDownloadAttachmentFile}
            handleSendCompliancePaymentRequest={handleSendCompliancePaymentRequest}
            handleSendCompliancePaymentResponse={handleSendCompliancePaymentResponse}
            handleOpenRejectPaymentModal={() => setDialogConfirmReturnPaymentIsOpen(true)}
          />
        )}
        {dialogConfirmReturnPaymentIsOpen && (
          <Dialog
            errors={errors}
            actionButtonWarningStyle
            handleDialogAction={handleReturnPayment}
            dialogTitle={t('confirmRejectPaymentModal.title')}
            dialogIsLoading={dialogConfirmReturnPaymentIsLoading}
            actionButtonText={t('confirmRejectPaymentModal.actionButtonText')}
            handleDialogOpenState={(openState: boolean) => {
              if (openState === false) {
                setErrors({})
              }

              setDialogConfirmReturnPaymentIsOpen(openState)
            }}
          >
            <div className="dialog-content-text">
              <div>{t('confirmRejectPaymentModal.instruction')}</div>
              <div style={{ padding: '0px 16px', boxSizing: 'border-box' }}>
                <TextField
                  fullWidth
                  multiline
                  variant="standard"
                  sx={textFieldWhiteBg}
                  id="filled-textarea"
                  value={paymentActionReason || ''}
                  error={!!errors.paymentActionReason}
                  helperText={errors.paymentActionReason}
                  onChange={(event) => {
                    setErrors({})
                    setPaymentActionReason(event.target.value.toUpperCase())
                  }}
                  label={t('confirmSetBackgroundResearchReasonModal.placeholder')}
                />
              </div>
            </div>
          </Dialog>
        )}
      </div>
    </div>
  )
}
